import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Calendar, Menu, X, ClipboardList, MessageCircle, Scissors } from 'lucide-react';
import Logo from './Logo';
import Magnetic from './ui/Magnetic';
import { BUSINESS } from '../lib/data';

const NAV = [
  { label: 'Services', to: '/#services', icon: Scissors },
  { label: 'Book', to: '/booking', icon: Calendar },
  { label: 'Contact', to: '/contact', icon: MessageCircle },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location]);

  return (
    <div className="sticky top-0 z-50">
      {/* brand hairline — always visible */}
      <div aria-hidden className="h-1 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500" />

      <header className={`transition-all duration-500 ${scrolled ? 'px-3 sm:px-5 pt-3' : 'px-0 pt-0'}`}>
        <div
          className={`mx-auto transition-all duration-500 ${
            scrolled
              ? 'max-w-6xl rounded-full bg-white/80 backdrop-blur-xl shadow-xl shadow-purple-200/40 ring-1 ring-pink-100 px-4 sm:px-6'
              : 'max-w-7xl bg-white/65 backdrop-blur-md px-4'
          }`}
        >
          <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? 'h-14 md:h-16' : 'h-16 md:h-20'}`}>
            <Link to="/" className="group flex items-center space-x-2 sm:space-x-3 min-w-0">
              <span className="transition-transform duration-500 group-hover:rotate-[-8deg] group-hover:scale-105">
                <Logo size="header" className="ring-2 ring-pink-100 shadow-pink-200/60" />
              </span>
              <span className="min-w-0">
                <span className="block font-display text-lg sm:text-xl md:text-2xl font-black bg-gradient-to-r from-pink-600 to-cyan-600 bg-clip-text text-transparent leading-tight truncate">
                  Bubbles &amp; Bark
                </span>
                <span className={`block text-[11px] md:text-xs font-bold text-gray-500 leading-tight truncate transition-all duration-500 ${scrolled ? 'opacity-0 h-0' : 'opacity-100'}`}>
                  {BUSINESS.tagline}
                </span>
              </span>
            </Link>

            {/* Desktop nav — animated gradient underline */}
            <nav className="hidden lg:flex items-center space-x-9">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} className="group relative py-2 text-[15px] font-extrabold text-ink-900 hover:text-pink-600 transition-colors">
                  {n.label}
                  <span aria-hidden className="absolute left-0 -bottom-0.5 h-[3px] w-0 group-hover:w-full rounded-full bg-gradient-to-r from-pink-500 to-cyan-500 transition-all duration-300" />
                </Link>
              ))}
            </nav>

            <div className="flex items-center space-x-1.5 md:space-x-3">
              {/* phone chip with gradient ring */}
              <a href={BUSINESS.phoneHref} className="hidden md:block rounded-full p-[1.5px] bg-gradient-to-r from-pink-400 to-cyan-400 hover:from-pink-500 hover:to-cyan-500 transition-colors">
                <span className="flex items-center space-x-2 bg-white rounded-full px-4 py-2 text-sm font-extrabold text-ink-900">
                  <Phone className="w-4 h-4 text-pink-500" />
                  <span>{BUSINESS.phone}</span>
                </span>
              </a>
              <Magnetic strength={0.2}>
                <Link
                  to="/intake-form"
                  className="btn-shine inline-flex items-center space-x-1.5 sm:space-x-2 bg-gradient-to-r from-pink-500 to-cyan-500 text-white px-3 md:px-6 py-2.5 rounded-full font-extrabold text-sm shadow-lg shadow-pink-200/60 hover:shadow-xl transition-all duration-300 hover:scale-105"
                >
                  <ClipboardList className="w-4 h-4 shrink-0" />
                  <span className="hidden sm:inline whitespace-nowrap">New Client Form</span>
                  <span className="sm:hidden whitespace-nowrap">Intake</span>
                </Link>
              </Magnetic>
              <button
                onClick={() => setOpen((v) => !v)}
                aria-label="Menu"
                className="lg:hidden p-1.5 sm:p-2 rounded-full hover:bg-pink-50 text-ink-900"
              >
                {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile menu — lives inside the capsule so it rounds nicely */}
          <AnimatePresence>
            {open && (
              <motion.nav
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.25 }}
                className="lg:hidden overflow-hidden border-t border-pink-100"
              >
                <div className="py-4 space-y-1">
                  {NAV.map((n) => (
                    <Link
                      key={n.label}
                      to={n.to}
                      className="flex items-center space-x-3 px-4 py-3 rounded-2xl font-extrabold text-ink-900 hover:bg-pink-50 hover:text-pink-600 transition-colors"
                    >
                      <n.icon className="w-5 h-5 text-pink-500" />
                      <span>{n.label}</span>
                    </Link>
                  ))}
                  <a
                    href={BUSINESS.phoneHref}
                    className="flex items-center space-x-3 px-4 py-3 rounded-2xl font-extrabold text-ink-900 hover:bg-pink-50 transition-colors"
                  >
                    <Phone className="w-5 h-5 text-pink-500" />
                    <span>{BUSINESS.phone}</span>
                  </a>
                </div>
              </motion.nav>
            )}
          </AnimatePresence>
        </div>
      </header>
    </div>
  );
}
